import { useCallback, useRef, useState } from 'react'
import {
  toggleSearchCategory,
  type AssistRoute,
} from '@/pages/Meetings/components/Copilot/assist.types'
import {
  DEFAULT_ASSIST_ROUTES,
  parseAssistRoutesStatus,
} from '@/pages/Meetings/components/Copilot/assistRoutes'

export type AssistRoutesErrorCode = 'bad_request' | 'not_supported'

interface UseAssistRoutesOptions {
  /** The reader's remembered choice, or undefined for anyone who has none. */
  stored: ReadonlySet<AssistRoute> | undefined
  /** Remember a choice for the meetings after this one. */
  onRemember: (routes: ReadonlySet<AssistRoute>) => void
  /**
   * Send `assist.routes.update` on every socket this screen owns.
   *
   * @returns False when no socket was ready to take the frame.
   */
  sendUpdate: (routes: ReadonlySet<AssistRoute>) => boolean
}

/**
 * Which kinds of question the Copilot answers, and the frames that settle it.
 *
 * These buttons do one job: they tell the AI service which routes to work.
 * They used to do a second — filtering the suggestion list already on screen —
 * and that half is gone. Hiding a suggestion locally only ever hid the
 * evidence that the service was still working a route the reader had turned
 * off.
 *
 * Unlike {@link useAssistModel}, a press still takes effect at once rather
 * than waiting for the acknowledgment. See {@link handleError} for why.
 *
 * `assist.routes.status` is therefore read for what only the server knows —
 * whether the Backlog side is usable at all — and not to overrule the reader.
 * Item 2 of the contract is explicit that a dimmed Backlog button keeps the
 * selection it had, so that a repaired ticket store does not cost the reader
 * a second press.
 */
export function useAssistRoutes({ stored, onRemember, sendUpdate }: UseAssistRoutesOptions) {
  const [routes, setRoutes] = useState<ReadonlySet<AssistRoute>>(
    stored ?? DEFAULT_ASSIST_ROUTES
  )
  const [backlogAvailable, setBacklogAvailable] = useState(true)
  const [backlogReason, setBacklogReason] = useState<string | null>(null)

  const routesRef = useRef(routes)
  const optionsRef = useRef({ onRemember, sendUpdate })
  optionsRef.current = { onRemember, sendUpdate }

  const commit = useCallback((next: ReadonlySet<AssistRoute>) => {
    routesRef.current = next
    setRoutes(next)
  }, [])

  /** The routes to put on the next `config`, including after a reconnect. */
  const getAssistRoutes = useCallback((): ReadonlySet<AssistRoute> => routesRef.current, [])

  /**
   * Turn one route on or off, because the reader pressed its button.
   *
   * The last route on stays on — `toggleSearchCategory` returns the same set
   * for that press, and a selection of nothing would leave the Copilot with
   * no way to answer anything.
   */
  const toggle = useCallback(
    (route: AssistRoute) => {
      const current = routesRef.current
      const next = toggleSearchCategory(current, route)
      if (next === current) return

      commit(next)
      optionsRef.current.onRemember(next)
      optionsRef.current.sendUpdate(next)
    },
    [commit]
  )

  /**
   * Apply `assist.routes.status` — after `ready`, after each accepted update,
   * and whenever `project.update` changes what the ticket store holds.
   *
   * Only the Backlog verdict is taken from it; see the note on the hook.
   *
   * @param event The frame.
   */
  const handleStatus = useCallback((event: unknown) => {
    const status = parseAssistRoutesStatus(event)
    if (!status) return
    setBacklogAvailable(status.backlogAvailable)
    setBacklogReason(status.backlogReason)
  }, [])

  /**
   * Note `assist.routes.update.error` and leave the buttons where the reader
   * put them.
   *
   * It used to put them back, copied from {@link useAssistModel} where that is
   * right — which model answers is the server's word. Reverting here made the
   * buttons unusable: a server that refuses the frame refuses every press, so
   * each one snapped back and neither route could be turned off at all. That
   * was reported as a bug, and it is the reason this stays as it is even now
   * that the buttons are nothing but a message to the server.
   *
   * So the screen can disagree with the service, and the warning below is the
   * only place that disagreement is recorded. A control that says what it was
   * told is worth less than one that answers the reader's press.
   *
   * @param code What the server refused it for.
   */
  const handleError = useCallback((code: AssistRoutesErrorCode) => {
    // The one trace this leaves. The routes the server is working have
    // silently parted company with the ones on screen, and nothing else on
    // this path says so.
    console.warn(`Assist routes refused by the server (${code}); keeping the reader's choice`)
  }, [])

  return {
    routes,
    backlogAvailable,
    backlogReason,
    getAssistRoutes,
    toggle,
    handleStatus,
    handleError,
  }
}
